<template>
  <div :class="['gu-datagrid-new gu-datagrid-server', props.class]">
    <grid-menu
      v-if="gridReady && !isSiteConfigLoading"
      :admin="isAdmin"
      :config="gridMenuConfig"
      :grid-api="gridApi"
      :initial-column-state="initialColumnState"
      :page-view-id="pageViewId"
      :default-queue-name="customParams.queueName"
      :user-role="userRole"
    />

    <applied-filters
      v-if="gridReady && !isSiteConfigLoading && showActiveFilters"
      :grid-api="gridApi"
    />

    <ag-grid-vue
      v-if="!isSiteConfigLoading && columnDefs.length"
      :grid-options="internalGridOptions"
      class="ag-grid-vue gu-datagrid-theme ag-theme-quartz "
      @grid-ready="handleGridReady"
      @pagination-changed="handlePaginationChanged"
      @selection-changed="handleSelectionChanged"
      @first-data-rendered="handleFirstDataRendered"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { AgGridVue } from 'ag-grid-vue3'

import AppliedFilters from '@gu-datagrid-new/components/AppliedFilters.vue'
import GridMenu from '@gu-datagrid-new/components/grid-menu/GridMenu.vue'

import { useGridConfig } from '@gu-datagrid-new/composables/GridConfig'
import { useGridOptions } from '@gu-datagrid-new/composables/GridOptions'
import { useGridState } from '@gu-datagrid-new/composables/GridState'
import { useAgGridLicense } from '@gu-datagrid-new/composables/LicenseManager'
import { usePagination } from '@gu-datagrid-new/composables/Pagination'
import { useSelection } from '@gu-datagrid-new/composables/Selection'
import { useServerSideDatasource } from '@gu-datagrid-new/composables/ServerSideDataSource'
import { useSiteConfigLoader } from '@gu-datagrid-new/composables/SiteConfigLoader'
import { useRowModelType } from '@gu-datagrid-new/composables/RowModelType'

const props = defineProps({
  config: {
    type: Object,
    default: () => ({
      rowsUrl: '',
      gridOptions: {
        columnDefs: [],
      },
      presetsSettings: {
        scope: 'config.presets',
        availableSiteScopes: [
          {
            'label': 'My Projects',
            'value': 'config.ic-applications-presets',
          },
          {
            'label': 'Dashboard Outstanding Actions',
            'value': 'config.dashboard-outstanding-actions',
          },
        ],
        roles: ['Role1', 'Role2', 'Role3', 'Role4'],
        preloadPresetByRole: { Role1: 1, Roles2: 10 },
      },
      // Whether to save and restore grid state (filters, sorting, pagination) from sessionStorage
      applyGridStateFromStorage: false,
    }),
    description: 'Datagrid configuration (both GU props and Ag-Grid options)',
  },
  siteConfigName: {
    type: String,
    default: null,
    description: 'Site config name that contains the grid configuration.',
  },
  class: {
    type: String,
    default: '',
  },
  queueName: {
    type: String,
    default: null,
    description: 'Optional queue name to override the one from site config.',
  },
})

const emits = defineEmits([
  'fail',
  'request-fired',
  'rows-selected',
  'success',
  'grid-ready',
  'first-data-rendered',
])

const route = useRoute()

useAgGridLicense()

const { 
  siteConfig,
  isLoading: isSiteConfigLoading,
  error: siteConfigError,
} = useSiteConfigLoader(props.siteConfigName)

const {
  customParams,
  gridMenuConfig,
  pinnedColumns,
  serverConfig,
  showActiveFilters,
} = useGridConfig(props, siteConfig)

const {
  columnDefs,
  gridOptions: baseGridOptions,
} = useGridOptions(siteConfig, props.config)

 
const { 
  onPaginationChanged,
  markInitialRequestComplete,
} = usePagination()
const { onSelectionChanged } = useSelection(emits)

const pageViewId = ref(null)

watch(() => siteConfigError.value, (newError) => {
  if (newError) {
    console.error('GuDatagridServer siteConfig error:', newError)
  }
})

const gridReady = ref(false)
const gridApi = ref(null)
const { rowModelType } = useRowModelType('serverSide')

const gridState = useGridState(props.config?.applyGridStateFromStorage)

const isAdmin = computed(() => window?.siteVars?.user?.privileges?.includes('editAllProjectFilters'))

const userRole = ref('Role1')

const initialColumnState = computed(() => {
  const columns = columnDefs.value.map((colDef, index) => ({
    colId: colDef.field || `col_${index}`,
    width: colDef.width || undefined,
    visible: colDef.hide !== true,
    pinned: pinnedColumns.value.includes(colDef.field) ? 'left' : undefined,
  }))

  return columns
})

const serverSideDatasource = computed(() => {
  if (isSiteConfigLoading.value) return false

  // initalize serverSideDatasource after content override is finish load 
  const { serverSideDatasource } = useServerSideDatasource(
    serverConfig.value,
    customParams.value,
    undefined,
    emits,
    gridApi,
    gridState,
  )
  return serverSideDatasource
})

const internalGridOptions = computed(() => {
  if (isSiteConfigLoading.value) return false

  const mergedGridOptions = {
    ...baseGridOptions.value,
    columnDefs: columnDefs.value,
    rowModelType: rowModelType.value,
    serverSideDatasource: serverSideDatasource.value,
    getRowId: (params) => {
      if (baseGridOptions?.value?.defaultColumnId) return String(params.data[baseGridOptions?.value?.defaultColumnId])
      return String(params.data.id)
    },
    onFilterChanged: (params) => {
      if (gridReady.value) {
        gridState.saveCurrentGridState(gridApi.value, null)
      }
      if (params.context?.esPaginationApiType === 'search_after') {
        delete params.context.searchAfter
        params.api.setGridOption('context', {
          ...params.context,
        })
      }
      params.api.paginationGoToFirstPage()
    },
    onSortChanged: (params) => {
      if (gridReady.value) {
        gridState.saveCurrentGridState(gridApi.value, null)
      }
      if (params.context?.esPaginationApiType === 'search_after') {
        delete params.context.searchAfter
        params.api.setGridOption('context', {
          ...params.context,
        })
      }
      params.api.paginationGoToFirstPage()
    },
  }
  return mergedGridOptions
})

const handleGridReady = (params) => {
  gridApi.value = params.api
  gridReady.value = true
  gridState.setURLorStorageFilterModel(params.api)

  gridState.syncFilterModel(params.api)
  emits('grid-ready', params)
}

const handlePaginationChanged = (event) => {
  try {
    onPaginationChanged(event, gridApi, gridState)
  } catch (error) {
    console.error('[Datagrid] Error in handlePaginationChanged:', error)
  }
}

const handleSelectionChanged = () => {
  try {
    onSelectionChanged(gridApi)
  } catch (error) {
    console.error('[Datagrid] Error in handleSelectionChanged:', error)
  }
}

const handleFirstDataRendered = () => {
  setTimeout(() => {
    emits('first-data-rendered', { gridApi, gridState })
    markInitialRequestComplete(gridApi, gridState)
  }, 0)
}

onBeforeMount(() => {
  const urlViewIdParam = Number(route.query?.viewId)

  if (urlViewIdParam) pageViewId.value = urlViewIdParam
})
</script>
